/**
 * Contesto della chat associati (area riservata): stato di accesso, elenco
 * delle conversazioni e tempo reale sul namespace socket `/chat`.
 *
 * Montato una volta sola attorno a tutte le route `/area-riservata/*` dell'associato
 * (`components/chat/ChatAreaRiservata.tsx`): il sito è «in uso» su tutta l'area, non solo
 * sulla pagina della chat (spec notifiche §8, §16 C1). Fuori dall'area non c'è.
 *
 * Il reducer è puro ed esportato (`chatReducer`, `azioni`) per i test.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getAssociateData, getToken } from '../services/otpAuthService';
import { erroreChat, getConversazioni, getStato } from '../services/chatService';
import { creaSegnalatoreAttivita, SegnalatoreAttivita } from '../utils/chatAttivita';
import { contaComeNonLetto } from '../utils/chatMessaggi';
import { anteprimaStickerDi, eSticker, EVENTO_STICKER_AGGIORNATI } from '../utils/chatSticker';
import { EVENTO_PERSONA_CAMBIATA, idPersonaChat, tokenPerRiconnessione } from '../utils/sezioniDipendente';
import type { MenzioneComposta } from '../utils/chatMenzioni';
import type {
  ChatConversazione,
  ChatMessaggio,
  ChatRisposta,
  ChatStato,
  EvConversazione,
  EvMessaggio,
  EvPresenza,
  EvRicevute,
  EvRisposta,
  EvScrive,
} from '../../../shared/types/chat';

const SOCKET_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/graphql')
  .replace('/graphql', '')
  .replace(/\/api$/, '');

const SCRIVE_MS = 6000;
const DEBOUNCE_CONVERSAZIONI_MS = 500;
const MAX_ANTEPRIMA = 120;
/** Come il server: il contatore delle menzioni non lette si ferma a 99. */
const MAX_MENZIONI_NON_LETTE = 99;
/** Dopo un rifiuto dell'handshake con chat ancora disponibile: al massimo un nuovo socket ogni 30 s. */
const RIAVVIO_MIN_MS = 30000;
/**
 * Chiusure ripetute dal server (`io server disconnect`, es. la scheda nascosta più vecchia scalzata
 * oltre le 5 connessioni): attesa raddoppiata a ogni chiusura (30 s, 60 s, 120 s…) fino a 5 minuti,
 * così le schede nascoste non si scalzano a vicenda a ciclo. Una connessione rimasta su per
 * 5 minuti riporta l'attesa a 30 s.
 */
const RIAVVIO_MAX_MS = 5 * 60 * 1000;
export const attesaRiavvio = (chiusure: number): number =>
  Math.min(RIAVVIO_MAX_MS, RIAVVIO_MIN_MS * 2 ** Math.max(0, chiusure));

/** Errori dell'handshake che dipendono dallo stato di accesso: si ricarica lo stato e non si riprova. */
const ERRORI_ACCESSO = ['REGOLE_DA_ACCETTARE', 'NON_IN_REGOLA', 'CHAT_SPENTA', 'CHAT_NON_DISPONIBILE', 'IMPERSONIFICAZIONE', 'SEZIONE_NON_ABILITATA', 'STUDIO_NON_IN_REGOLA'];
/**
 * Dipendente dello studio (piano 2): `/stato` risponde 403 con questi codici (sezione `chat` tolta dal
 * titolare, studio non in regola). Non sono errori ma stati della chat: si riportano come motivo.
 */
const MOTIVI_DIPENDENTE: Record<string, ChatStato['motivo']> = {
  SEZIONE_NON_ABILITATA: 'sezione_non_abilitata',
  STUDIO_NON_IN_REGOLA: 'studio_non_in_regola',
};
const statoNonDisponibile = (motivo: ChatStato['motivo'], precedente: ChatStato | null): ChatStato => ({
  disponibile: false,
  motivo,
  regole: precedente && precedente.regole ? precedente.regole : { versione: 0, testo: '', accettate: false },
  profilo: null,
  nonLetti: 0,
});
/** Troppe schede o dispositivi collegati: avviso e nuovo tentativo, al massimo uno ogni 30 s. */
const TROPPE_CONNESSIONI = 'TROPPE_CONNESSIONI';
/** Sessione scaduta: l'avviso resta finché il socket non si riconnette. */
const SESSIONE_SCADUTA = 'TOKEN_NON_VALIDO';
/** Elenco caricato da meno di così alla prima connessione del socket: non si ricarica di nuovo. */
const ELENCO_RECENTE_MS = 2000;

/**
 * Rifiuto del middleware del server (`next(new Error(CODICE))`): il client
 * socket.io lo consegna con `data` e NON riprova da solo. Gli errori di rete
 * (`xhr poll error`, `websocket error`, `timeout`) invece si ritentano da soli.
 */
export const rifiutoDelServer = (err: any): boolean => {
  if (!err) return false;
  if (Object.prototype.hasOwnProperty.call(err, 'data')) return true;
  return typeof err.message === 'string' && /^[A-Z][A-Z0-9_]+$/.test(err.message);
};

export interface ErroreContestoChat { codice: string | null; messaggio: string }

export interface ChatSocket {
  emit(ev: string, dati?: unknown): void;
  /** Restituisce la funzione di rimozione. */
  on(ev: string, fn: (d: any) => void): () => void;
}

/** Quello che riceve il pannello delle risposte: una risposta nuova o aggiornata della sua radice. */
export interface EventoRisposta { tipo: 'risposta' | 'risposta_aggiornata'; risposta: ChatRisposta }

export interface ChatCtx {
  stato: ChatStato | null;
  /** Lettura dello stato di accesso (`/stato`) in corso: non riguarda l'elenco. */
  caricamento: boolean;
  /** Primo esito dell'elenco delle conversazioni arrivato (riuscito o fallito): prima, scheletro. */
  elencoCaricato: boolean;
  errore: ErroreContestoChat | null;
  conversazioni: ChatConversazione[];
  totaleNonLetti: number;
  connesso: boolean;
  /** Socket rifiutato per troppe finestre della chat aperte (finché non si riconnette). */
  troppeConnessioni: boolean;
  scrivono: Record<string, boolean>;
  /** Nei canali: chi sta scrivendo (l'ultimo arrivato), per conversazione. */
  nomiScrivono: Record<string, string>;
  attiva: string | null;
  ricaricaStato(): Promise<void>;
  ricaricaConversazioni(): Promise<void>;
  aggiornaConversazione(c: ChatConversazione): void;
  rimuoviConversazione(id: string): void;
  azzeraNonLetti(id: string): void;
  segnaAttiva(id: string | null): void;
  /** Testo non inviato di una conversazione (conservato finché si resta nell'area riservata). */
  leggiBozza(id: string): string;
  /** Menzioni «@» della bozza (intervalli sul testo di `leggiBozza`), [] se nessuna. */
  leggiMenzioniBozza(id: string): MenzioneComposta[];
  salvaBozza(id: string, testo: string, menzioni?: MenzioneComposta[]): void;
  socket: ChatSocket;
  /**
   * Risposte di un annuncio in tempo reale, per il pannello aperto: `cb` riceve solo quelle di `radiceId`;
   * restituisce la funzione di rimozione. Il conteggio sotto l'annuncio lo aggiorna `useConversazione`
   * (`risposte_conteggio`). Qui una riconnessione non recupera nulla: il pannello rilegge da sé quando il socket torna.
   */
  sottoscriviRisposte(radiceId: string, cb: (e: EventoRisposta) => void): () => void;
  /**
   * Cresce quando il catalogo degli sticker va riletto: evento `sticker-aggiornati` (catalogo cambiato
   * dall'ufficio), riconnessione del socket (evento perso), evento `conversazione` della chat aperta
   * (sticker o riservati del canale cambiati), invio rifiutato con `STICKER_*`. Facoltativo per i test.
   */
  versioneSticker?: number;
  /** Il catalogo degli sticker va riletto subito (invio rifiutato con `STICKER_*`). Facoltativo per i test. */
  ricaricaSticker?: () => void;
}

// --- Funzioni pure ---

/** Ritardo casuale (1-5 s) delle riletture del catalogo degli sticker chieste da eventi che arrivano a tutti. */
export const ritardoStickerCasuale = (): number => 1000 + Math.floor(Math.random() * 4000);

const tempo = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return isNaN(t) ? 0 : t;
};

/** Taglio per code point (come il backend): mai un'emoji spezzata a metà. */
const taglia = (s: string): string => {
  const una = String(s || '').replace(/\s*\n\s*/g, ' ').trim();
  const punti = Array.from(una);
  return punti.length > MAX_ANTEPRIMA ? punti.slice(0, MAX_ANTEPRIMA - 1).join('') + '…' : una;
};

/** Anteprima di un messaggio per l'elenco (stesse regole del backend). */
export const anteprimaMessaggio = (m: ChatMessaggio): string => {
  if (m.eliminato) return 'Messaggio eliminato';
  if (m.rimosso) return 'Messaggio rimosso';
  // lo sticker ha anche l'allegato di compatibilità (immagine): si guarda prima il tipo
  if (eSticker(m)) return anteprimaStickerDi(m);
  if (m.tipo === 'immagine') return taglia(m.testo ? `📷 Foto: ${m.testo}` : '📷 Foto');
  // audio e video sono documenti con `allegato.genere` (contratto §1.5)
  const genere = m.tipo === 'documento' && m.allegato ? m.allegato.genere : null;
  if (genere === 'audio') return taglia(m.testo ? `🎵 Audio: ${m.testo}` : '🎵 Audio');
  if (genere === 'video') return taglia(m.testo ? `🎬 Video: ${m.testo}` : '🎬 Video');
  if (m.tipo === 'documento') return taglia(`📄 ${(m.allegato && m.allegato.nomeFile) || 'Documento'}`);
  return taglia(m.testo);
};

/** Fissate in cima, poi ultima attività decrescente (le conversazioni senza messaggi in fondo). */
export const ordinaConversazioni = (lista: ChatConversazione[]): ChatConversazione[] =>
  lista.slice().sort((a, b) => {
    if (a.fissata !== b.fissata) return a.fissata ? -1 : 1;
    return tempo(b.ultimoMessaggio && b.ultimoMessaggio.data) - tempo(a.ultimoMessaggio && a.ultimoMessaggio.data);
  });

const silenziata = (c: ChatConversazione, ora: number): boolean => !!c.silenziataFino && tempo(c.silenziataFino) > ora;

/** Somma dei non letti delle conversazioni non archiviate e non silenziate. */
export const contaNonLetti = (lista: ChatConversazione[], ora: number = Date.now()): number =>
  lista.reduce((tot, c) => (c.archiviata || silenziata(c, ora) ? tot : tot + (c.nonLetti || 0)), 0);

// --- Reducer ---

export interface StatoChat {
  stato: ChatStato | null;
  caricamento: boolean;
  elencoCaricato: boolean;
  errore: ErroreContestoChat | null;
  conversazioni: ChatConversazione[];
  connesso: boolean;
  scrivono: Record<string, boolean>;
  nomiScrivono: Record<string, string>;
  attiva: string | null;
  /** Scheda del browser nascosta: la chat aperta non si legge, i suoi messaggi contano come non letti. */
  nascosta: boolean;
}

export const statoIniziale: StatoChat = {
  stato: null,
  caricamento: true,
  elencoCaricato: false,
  errore: null,
  conversazioni: [],
  connesso: false,
  scrivono: {},
  nomiScrivono: {},
  attiva: null,
  nascosta: false,
};

/**
 * Il mio id persona (dati salvati all'accesso), per riconoscere le menzioni che mi riguardano: l'id dello
 * studio per il titolare (come prima), quello del dipendente per il dipendente (piano 2, chat per persona).
 */
const mioIdPersona = (): string | null => {
  try {
    return idPersonaChat(getAssociateData()) || null;
  } catch {
    return null;
  }
};

const schedaNascosta = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Input veri che contano per la presenza «come WhatsApp Web» (il segnalatore ne limita gli invii).
 * `scroll` a parte (`segnalatore.scorrimento()`): lo genera anche lo scorrimento automatico
 * all'arrivo di un messaggio, quindi conta solo entro 2 s da uno di questi (trascinamento della barra).
 */
const EVENTI_INTERAZIONE = ['mousemove', 'mousedown', 'pointerdown', 'keydown', 'touchstart', 'touchmove', 'wheel', 'focusin'];

export type AzioneChat =
  | { tipo: 'caricamento'; valore: boolean }
  | { tipo: 'elencoCaricato' }
  | { tipo: 'stato'; stato: ChatStato }
  | { tipo: 'errore'; errore: ErroreContestoChat | null }
  | { tipo: 'conversazioniCaricate'; conversazioni: ChatConversazione[] }
  | { tipo: 'aggiornaConversazione'; conversazione: ChatConversazione }
  | { tipo: 'rimuoviConversazione'; id: string }
  | { tipo: 'azzeraNonLetti'; id: string }
  | { tipo: 'messaggio'; evento: EvMessaggio; mioId?: string | null }
  | { tipo: 'messaggioAggiornato'; evento: EvMessaggio }
  | { tipo: 'ricevute'; evento: EvRicevute }
  | { tipo: 'scrive'; conversazioneId: string; valore: boolean; nome?: string }
  | { tipo: 'presenza'; evento: EvPresenza }
  | { tipo: 'connesso'; valore: boolean }
  | { tipo: 'attiva'; id: string | null }
  | { tipo: 'nascosta'; valore: boolean };

export const azioni = {
  caricamento: (valore: boolean): AzioneChat => ({ tipo: 'caricamento', valore }),
  elencoCaricato: (): AzioneChat => ({ tipo: 'elencoCaricato' }),
  stato: (stato: ChatStato): AzioneChat => ({ tipo: 'stato', stato }),
  errore: (errore: ErroreContestoChat | null): AzioneChat => ({ tipo: 'errore', errore }),
  conversazioniCaricate: (conversazioni: ChatConversazione[]): AzioneChat => ({ tipo: 'conversazioniCaricate', conversazioni }),
  aggiornaConversazione: (conversazione: ChatConversazione): AzioneChat => ({ tipo: 'aggiornaConversazione', conversazione }),
  rimuoviConversazione: (id: string): AzioneChat => ({ tipo: 'rimuoviConversazione', id }),
  azzeraNonLetti: (id: string): AzioneChat => ({ tipo: 'azzeraNonLetti', id }),
  /** `mioId`: il mio id persona, per contare i messaggi dei canali che mi menzionano (senza, non si contano). */
  messaggio: (evento: EvMessaggio, mioId?: string | null): AzioneChat => ({ tipo: 'messaggio', evento, mioId }),
  messaggioAggiornato: (evento: EvMessaggio): AzioneChat => ({ tipo: 'messaggioAggiornato', evento }),
  ricevute: (evento: EvRicevute): AzioneChat => ({ tipo: 'ricevute', evento }),
  scrive: (conversazioneId: string, valore: boolean, nome?: string): AzioneChat => ({ tipo: 'scrive', conversazioneId, valore, ...(nome ? { nome } : {}) }),
  presenza: (evento: EvPresenza): AzioneChat => ({ tipo: 'presenza', evento }),
  connesso: (valore: boolean): AzioneChat => ({ tipo: 'connesso', valore }),
  attiva: (id: string | null): AzioneChat => ({ tipo: 'attiva', id }),
  nascosta: (valore: boolean): AzioneChat => ({ tipo: 'nascosta', valore }),
};

const conScrive = (scrivono: Record<string, boolean>, id: string, valore: boolean): Record<string, boolean> =>
  scrivono[id] === valore || (!valore && scrivono[id] === undefined) ? scrivono : { ...scrivono, [id]: valore };

/** Nome di chi scrive nei canali: messo con `nome`, tolto quando smette. */
const conNome = (nomi: Record<string, string>, id: string, nome: string | null): Record<string, string> => {
  if (nome) return nomi[id] === nome ? nomi : { ...nomi, [id]: nome };
  if (!(id in nomi)) return nomi;
  const altri = { ...nomi };
  delete altri[id];
  return altri;
};

/** Applica `fn` alla conversazione `id`; se `fn` restituisce la stessa, lo stato non cambia. */
const modifica = (
  s: StatoChat,
  id: string,
  fn: (c: ChatConversazione) => ChatConversazione,
  riordina = false
): ChatConversazione[] => {
  let cambiata = false;
  const lista = s.conversazioni.map((c) => {
    if (c.id !== id) return c;
    const nuova = fn(c);
    if (nuova !== c) cambiata = true;
    return nuova;
  });
  if (!cambiata) return s.conversazioni;
  return riordina ? ordinaConversazioni(lista) : lista;
};

export const chatReducer = (s: StatoChat, a: AzioneChat): StatoChat => {
  switch (a.tipo) {
    case 'caricamento':
      return { ...s, caricamento: a.valore };
    case 'elencoCaricato':
      return s.elencoCaricato ? s : { ...s, elencoCaricato: true };
    case 'stato':
      return { ...s, stato: a.stato };
    case 'errore':
      return { ...s, errore: a.errore };
    case 'conversazioniCaricate':
      return { ...s, conversazioni: ordinaConversazioni(a.conversazioni) };
    case 'aggiornaConversazione': {
      const c = a.conversazione;
      const presente = s.conversazioni.some((x) => x.id === c.id);
      const lista = presente ? s.conversazioni.map((x) => (x.id === c.id ? c : x)) : [...s.conversazioni, c];
      return { ...s, conversazioni: ordinaConversazioni(lista) };
    }
    case 'rimuoviConversazione':
      return { ...s, conversazioni: s.conversazioni.filter((c) => c.id !== a.id) };
    case 'azzeraNonLetti':
      return {
        ...s,
        conversazioni: modifica(s, a.id, (c) => (c.nonLetti || c.menzioniNonLette ? { ...c, nonLetti: 0, menzioniNonLette: 0 } : c)),
      };
    case 'messaggio': {
      const { conversazioneId, messaggio: m } = a.evento;
      // Avvisi di sistema (es. «… è di nuovo nel canale»): come sul server non cambiano l'anteprima,
      // l'ordine dell'elenco né i non letti; la conversazione aperta li riceve per conto suo.
      if (m.tipo === 'sistema') return s;
      const conversazioni = modifica(s, conversazioneId, (c) => {
        const ultimo = c.ultimoMessaggio;
        const piuRecente = !ultimo || tempo(m.creatoIl) >= tempo(ultimo.data);
        // stessa regola del backend; la chat aperta in una scheda nascosta non si sta leggendo
        const conta = contaComeNonLetto(m) && (s.attiva !== conversazioneId || s.nascosta);
        const nonLetti = conta ? (c.nonLetti || 0) + 1 : c.nonLetti;
        // menzioni (18/10/2026): il server non manda un evento apposito, si conta qui fino alla prossima lettura
        const miMenziona = conta && c.tipo === 'canale' && !!a.mioId && Array.isArray(m.menzioni) && m.menzioni.some((x) => x && x.id === a.mioId);
        const menzioni = miMenziona ? { menzioniNonLette: Math.min(MAX_MENZIONI_NON_LETTE, (c.menzioniNonLette || 0) + 1) } : {};
        return {
          ...c,
          nonLetti,
          ...menzioni,
          ultimoMessaggio: piuRecente
            ? { anteprima: anteprimaMessaggio(m), data: m.creatoIl, mio: m.mio, stato: m.mio ? 'inviato' : null, mittenteNome: m.mittente.nome }
            : ultimo,
        };
      }, true);
      return { ...s, conversazioni, scrivono: conScrive(s.scrivono, conversazioneId, false), nomiScrivono: conNome(s.nomiScrivono, conversazioneId, null) };
    }
    case 'messaggioAggiornato': {
      const { conversazioneId, messaggio: m } = a.evento;
      return {
        ...s,
        conversazioni: modifica(s, conversazioneId, (c) => {
          const ultimo = c.ultimoMessaggio;
          if (!ultimo || tempo(ultimo.data) !== tempo(m.creatoIl)) return c;
          const anteprima = anteprimaMessaggio(m);
          return anteprima === ultimo.anteprima ? c : { ...c, ultimoMessaggio: { ...ultimo, anteprima } };
        }),
      };
    }
    case 'ricevute': {
      const e = a.evento;
      return {
        ...s,
        conversazioni: modifica(s, e.conversazioneId, (c) => {
          const ultimo = c.ultimoMessaggio;
          // spunte solo nelle dirette e solo per le ricevute dell'altro partecipante
          if (!ultimo || !ultimo.mio || c.tipo !== 'diretta') return c;
          if (c.altro && e.associato && c.altro.id !== e.associato) return c;
          const data = tempo(ultimo.data);
          let stato = ultimo.stato;
          if (e.lettoFino && tempo(e.lettoFino) >= data) stato = 'letto';
          else if (e.consegnatoFino && tempo(e.consegnatoFino) >= data && stato !== 'letto') stato = 'consegnato';
          return stato === ultimo.stato ? c : { ...c, ultimoMessaggio: { ...ultimo, stato } };
        }),
      };
    }
    case 'scrive':
      return {
        ...s,
        scrivono: conScrive(s.scrivono, a.conversazioneId, a.valore),
        nomiScrivono: conNome(s.nomiScrivono, a.conversazioneId, a.valore ? a.nome || null : null),
      };
    case 'presenza': {
      const e = a.evento;
      let cambiata = false;
      const conversazioni = s.conversazioni.map((c) => {
        if (c.tipo !== 'diretta' || !c.altro || c.altro.id !== e.associato) return c;
        cambiata = true;
        return { ...c, altro: { ...c.altro, online: e.online, ultimoAccesso: e.ultimoAccesso } };
      });
      return cambiata ? { ...s, conversazioni } : s;
    }
    case 'connesso':
      // senza socket non si sa più chi sta scrivendo
      return { ...s, connesso: a.valore, scrivono: a.valore ? s.scrivono : {}, nomiScrivono: a.valore ? s.nomiScrivono : {} };
    case 'attiva':
      return { ...s, attiva: a.id };
    case 'nascosta':
      return s.nascosta === a.valore ? s : { ...s, nascosta: a.valore };
    default:
      return s;
  }
};

// --- Provider ---

const ChatContext = createContext<ChatCtx | undefined>(undefined);

/**
 * `attivo` (predefinito true): false = nessuna lettura dello stato e nessun socket (es. associato
 * non autenticato); se diventa false il socket si chiude.
 */
export const ChatProvider: React.FC<{ children: React.ReactNode; attivo?: boolean }> = ({ children, attivo = true }) => {
  const [s, dispatch] = useReducer(chatReducer, statoIniziale, (iniziale) => ({ ...iniziale, nascosta: schedaNascosta() }));

  const stRef = useRef(s);
  stRef.current = s;
  const montato = useRef(true);
  const socketRef = useRef<Socket | null>(null);
  const ascoltatori = useRef<Map<string, Set<(d: any) => void>>>(new Map());
  const inoltrati = useRef<Set<string>>(new Set());
  const inoltraRef = useRef<((ev: string) => void) | null>(null);
  const timerScrive = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const timerConversazioni = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqConversazioni = useRef(0);
  const seqStato = useRef(0);
  /** Cresce a ogni modifica locale dell'elenco: se cambia durante una ricarica, la risposta è già vecchia. */
  const modificheElenco = useRef(0);
  /** Generazione del socket: incrementandola l'effetto lo riapre. */
  const [generazione, setGenerazione] = useState(0);
  const ultimoAvvioSocket = useRef(0);
  const [troppeConnessioni, setTroppeConnessioni] = useState(false);
  const [versioneSticker, setVersioneSticker] = useState(0);
  /** Rilettura del catalogo degli sticker già programmata (eventi in raffica: una sola). */
  const timerSticker = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Versione dell'ultimo `sticker-aggiornati` gestito: lo stesso evento ripetuto non rilegge. */
  const ultimaVersioneSticker = useRef<string | null>(null);
  /**
   * Rilegge il catalogo degli sticker: subito, oppure dopo `ritardoMs` (eventi che arrivano a tutti i client
   * insieme: ritardo casuale, e le richieste che arrivano mentre una è già programmata si uniscono a quella).
   */
  const chiediSticker = useCallback((ritardoMs = 0) => {
    if (ritardoMs > 0 && timerSticker.current) return;
    if (timerSticker.current) clearTimeout(timerSticker.current);
    timerSticker.current = null;
    const rileggi = () => {
      timerSticker.current = null;
      if (montato.current) setVersioneSticker((v) => v + 1);
    };
    if (ritardoMs > 0) timerSticker.current = setTimeout(rileggi, ritardoMs);
    else rileggi();
  }, []);
  const ricaricaSticker = useCallback(() => chiediSticker(0), [chiediSticker]);
  const timerRiavvio = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Chiusure dal server consecutive (per l'attesa crescente del riavvio). */
  const chiusureDalServer = useRef(0);
  /** Scheda nascosta chiusa dal server: si ricollega solo quando torna visibile (nessun timer). */
  const attesaVisibilita = useRef(false);
  /** 5 minuti di connessione continua con la scheda visibile: le chiusure dal server ripartono da 30 s. */
  const timerStabile = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fermaStabile = useCallback(() => {
    if (timerStabile.current) clearTimeout(timerStabile.current);
    timerStabile.current = null;
  }, []);
  const avviaStabile = useCallback(() => {
    fermaStabile();
    if (schedaNascosta()) return;
    timerStabile.current = setTimeout(() => {
      timerStabile.current = null;
      chiusureDalServer.current = 0;
    }, RIAVVIO_MAX_MS);
  }, [fermaStabile]);
  /** Presenza del sito verso il socket attuale (evento `attivita`). */
  const segnalatoreRef = useRef<SegnalatoreAttivita | null>(null);
  /** Ricariche dell'elenco in volo e ora dell'ultima riuscita (per la prima connessione del socket). */
  const ricaricheInVolo = useRef(0);
  const ultimaRicaricaRiuscita = useRef(0);
  /** Testi non inviati per conversazione (solo in memoria), con le loro menzioni «@». */
  const bozze = useRef<Map<string, string>>(new Map());
  const menzioniBozze = useRef<Map<string, MenzioneComposta[]>>(new Map());
  /** Avviso di sessione scaduta in corso: lo toglie solo una riconnessione del socket. */
  const sessioneScaduta = useCallback(
    () => !!stRef.current.errore && stRef.current.errore.codice === SESSIONE_SCADUTA,
    []
  );

  useEffect(() => {
    montato.current = true;
    const scrive = timerScrive.current;
    return () => {
      montato.current = false;
      scrive.forEach((t) => clearTimeout(t));
      scrive.clear();
      if (timerConversazioni.current) clearTimeout(timerConversazioni.current);
      if (timerSticker.current) clearTimeout(timerSticker.current);
      if (timerRiavvio.current) clearTimeout(timerRiavvio.current);
      if (timerStabile.current) clearTimeout(timerStabile.current);
    };
  }, []);

  const ricaricaStatoRef = useRef<() => Promise<void>>(async () => {});

  /** Rilegge lo stato; restituisce lo stato letto, oppure null se la lettura è fallita. */
  const leggiStato = useCallback(async (): Promise<ChatStato | null> => {
    const seq = ++seqStato.current;
    try {
      const stato = await getStato();
      if (montato.current && seq === seqStato.current) {
        dispatch(azioni.stato(stato));
        if (!sessioneScaduta()) dispatch(azioni.errore(null));
      }
      return stato;
    } catch (err) {
      const e = erroreChat(err);
      const motivo = e.codice ? MOTIVI_DIPENDENTE[e.codice] : undefined;
      if (motivo) {
        // dipendente senza la chat o studio non in regola: stato «non disponibile», non un errore
        const stato = statoNonDisponibile(motivo, stRef.current.stato);
        if (montato.current && seq === seqStato.current) {
          dispatch(azioni.stato(stato));
          if (!sessioneScaduta()) dispatch(azioni.errore(null));
        }
        return stato;
      }
      if (montato.current && seq === seqStato.current && !sessioneScaduta()) {
        dispatch(azioni.errore({ codice: e.codice, messaggio: e.messaggio }));
      }
      return null;
    } finally {
      if (montato.current && seq === seqStato.current) dispatch(azioni.caricamento(false));
    }
  }, [sessioneScaduta]);

  const ricaricaStato = useCallback(async () => {
    await leggiStato();
  }, [leggiStato]);
  ricaricaStatoRef.current = ricaricaStato;

  const ricaricaConversazioniRef = useRef<() => Promise<void>>(async () => {});

  const ricaricaConversazioni = useCallback(async () => {
    const seq = ++seqConversazioni.current;
    const modificheAllInizio = modificheElenco.current;
    ricaricheInVolo.current += 1;
    try {
      const lista = await getConversazioni();
      if (!montato.current || seq !== seqConversazioni.current) return;
      ultimaRicaricaRiuscita.current = Date.now();
      dispatch(azioni.conversazioniCaricate(lista));
      dispatch(azioni.elencoCaricato());
      if (stRef.current.errore && !sessioneScaduta()) dispatch(azioni.errore(null));
      // eventi o modifiche arrivati mentre la richiesta era in volo: la risposta può non
      // includerli e li ha sovrascritti, quindi si ricarica una volta ancora
      if (modificheElenco.current !== modificheAllInizio) ricaricaConversazioniRef.current();
    } catch (err) {
      if (!montato.current || seq !== seqConversazioni.current) return;
      dispatch(azioni.elencoCaricato()); // esito arrivato, anche se fallito: niente scheletro infinito
      const e = erroreChat(err);
      if (!sessioneScaduta()) dispatch(azioni.errore({ codice: e.codice, messaggio: e.messaggio }));
      // accesso cambiato nel frattempo (regole nuove, tesseramento, chat spenta): si rilegge lo stato
      if (e.codice && ERRORI_ACCESSO.includes(e.codice)) ricaricaStatoRef.current();
    } finally {
      ricaricheInVolo.current -= 1;
    }
  }, [sessioneScaduta]);
  ricaricaConversazioniRef.current = ricaricaConversazioni;

  /** Applica una modifica locale all'elenco, annotandola per le ricariche in volo. */
  const modificaElenco = useCallback((azione: AzioneChat) => {
    modificheElenco.current += 1;
    dispatch(azione);
  }, []);

  const fermaScrive = useCallback((id: string) => {
    const t = timerScrive.current.get(id);
    if (t) clearTimeout(t);
    timerScrive.current.delete(id);
  }, []);

  // Avvio: stato di accesso (solo se attivo)
  useEffect(() => {
    if (attivo) ricaricaStato();
  }, [attivo, ricaricaStato]);

  const abilitata = attivo && !!(s.stato && s.stato.disponibile && s.stato.regole && s.stato.regole.accettate);

  // Elenco e socket solo con chat disponibile e regole accettate
  useEffect(() => {
    if (!abilitata) return undefined;
    ricaricaConversazioni();

    ultimoAvvioSocket.current = Date.now();
    // Token della persona con cui la chat è stata caricata: le riconnessioni usano il token attuale solo se è
    // della stessa persona (rinnovato). Cambio di persona o uscita in un'altra scheda: niente riconnessione.
    const tokenAvvio = getToken();
    // Chiusura definita PRIMA di io(): `auth` la può chiamare anche se socket.io la invocasse subito, dentro io(),
    // con `sock` non ancora assegnato (allora il socket si chiude appena io() lo restituisce).
    let chiuso = false;
    let sockAperto: ReturnType<typeof io> | null = null;
    const chiudi = () => {
      if (chiuso) return;
      chiuso = true;
      if (sockAperto) sockAperto.disconnect();
    };
    /** Il token attuale non è più della persona con cui la chat è partita (cambio o uscita in un'altra scheda). */
    const personaFinita = () => {
      chiudi();
      dispatch(azioni.connesso(false));
      dispatch(azioni.errore({ codice: SESSIONE_SCADUTA, messaggio: 'Sessione scaduta: accedi di nuovo all\'area riservata.' }));
    };
    const sock = io(`${SOCKET_BASE}/chat`, {
      // funzione: ogni (ri)connessione legge il token attuale; `client: 'web'` per la regola del PC
      auth: (cb: (dati: object) => void) => {
        const token = tokenPerRiconnessione(tokenAvvio, getToken());
        if (token) {
          cb({ token, client: 'web' });
          return;
        }
        // nessun connect: socket chiuso, come una sessione scaduta (la scheda la porta via useCambioPersonaAltraScheda)
        personaFinita();
      },
      transports: ['websocket', 'polling'],
    });
    sockAperto = sock;
    if (chiuso) sock.disconnect();
    socketRef.current = sock;
    // Annuncio di useCambioPersonaAltraScheda: il socket aperto non resta alla persona di prima fino alla riconnessione
    const cambioPersona = () => {
      if (!chiuso && !tokenPerRiconnessione(tokenAvvio, getToken())) personaFinita();
    };
    window.addEventListener(EVENTO_PERSONA_CAMBIATA, cambioPersona);
    inoltrati.current = new Set();
    const timerScriveAttivi = timerScrive.current;

    const inoltra = (ev: string) => {
      if (inoltrati.current.has(ev)) return;
      inoltrati.current.add(ev);
      sock.on(ev, (d: any) => {
        const fns = ascoltatori.current.get(ev);
        if (fns) Array.from(fns).forEach((fn) => fn(d));
      });
    };
    ascoltatori.current.forEach((_fns, ev) => inoltra(ev));
    inoltraRef.current = inoltra;

    let corrente = true;
    let giaConnesso = false;

    // Presenza «come WhatsApp Web»: scheda visibile + interazione recente (le push le decide il server)
    const segnalatore = creaSegnalatoreAttivita({
      invia: (dati) => sock.emit('attivita', dati),
      visibile: () => !schedaNascosta(),
    });
    segnalatoreRef.current = segnalatore;
    const interazione = () => segnalatore.interazione();
    const scorrimento = () => segnalatore.scorrimento();
    const sfocato = () => segnalatore.sfocato();
    EVENTI_INTERAZIONE.forEach((ev) => document.addEventListener(ev, interazione, { capture: true, passive: true }));
    // `scroll` non risale: in cattura arriva anche quello degli elenchi interni
    document.addEventListener('scroll', scorrimento, { capture: true, passive: true });
    window.addEventListener('focus', interazione);
    window.addEventListener('blur', sfocato);
    attesaVisibilita.current = false;

    /**
     * Socket che non riprova da solo (rifiuto del middleware, chiusura dal server):
     * si rilegge lo stato. Chat non più disponibile: l'effetto si chiude da solo e
     * riparte quando torna disponibile; ancora disponibile (o lettura fallita):
     * nuovo socket, al massimo uno ogni 30 s dall'avvio del socket.
     * Chiusura dal server (`chiusuraDalServer`: attesa crescente): l'attesa si conta dalla chiusura
     * e una scheda nascosta non si ricollega finché non torna visibile (allora subito).
     */
    const rileggiERiavvia = (chiusuraDalServer?: number) => {
      const dalServer = typeof chiusuraDalServer === 'number';
      const scadenza = dalServer ? Date.now() + (chiusuraDalServer as number) : ultimoAvvioSocket.current + RIAVVIO_MIN_MS;
      /** Scheda nascosta chiusa dal server: niente timer, si aspetta `visibilitychange`. */
      const aspettaVisibilita = () => {
        if (!dalServer || !schedaNascosta()) return false;
        attesaVisibilita.current = true;
        return true;
      };
      chiudi();
      dispatch(azioni.connesso(false));
      leggiStato().then((stato) => {
        if (!corrente || !montato.current) return;
        if (stato && !(stato.disponibile && stato.regole && stato.regole.accettate)) {
          setTroppeConnessioni(false);
          return;
        }
        if (timerRiavvio.current || aspettaVisibilita()) return;
        timerRiavvio.current = setTimeout(() => {
          timerRiavvio.current = null;
          if (!corrente || !montato.current || aspettaVisibilita()) return;
          setGenerazione((g) => g + 1);
        }, Math.max(0, scadenza - Date.now()));
      });
    };

    sock.on('connect', () => {
      segnalatore.connesso();
      avviaStabile();
      dispatch(azioni.connesso(true));
      setTroppeConnessioni(false);
      if (sessioneScaduta()) dispatch(azioni.errore(null));
      const { attiva, nascosta } = stRef.current;
      if (attiva && !nascosta) sock.emit('attiva', { conversazioneId: attiva });
      const prima = !giaConnesso;
      giaConnesso = true;
      // riconnessione: un `sticker-aggiornati` può essersi perso (ritardo casuale: dopo un riavvio del server
      // si riconnettono tutti insieme)
      if (!prima) chiediSticker(ritardoStickerCasuale());
      // prima connessione con l'elenco appena caricato (o in caricamento): niente seconda richiesta
      if (prima && (ricaricheInVolo.current > 0 || Date.now() - ultimaRicaricaRiuscita.current < ELENCO_RECENTE_MS)) return;
      ricaricaConversazioni();
    });
    sock.on('disconnect', (motivo: string) => {
      segnalatore.disconnesso();
      fermaStabile();
      dispatch(azioni.connesso(false));
      // chiusura decisa dal server: il client non si riconnette da solo; attesa crescente
      if (motivo === 'io server disconnect' && !chiuso) {
        const attesa = attesaRiavvio(chiusureDalServer.current);
        chiusureDalServer.current += 1;
        rileggiERiavvia(attesa);
      }
    });
    sock.on('connect_error', (err: any) => {
      const codice = err && typeof err.message === 'string' ? err.message : '';
      if (codice === SESSIONE_SCADUTA) {
        chiudi();
        dispatch(azioni.connesso(false));
        dispatch(azioni.errore({ codice, messaggio: 'Sessione scaduta: accedi di nuovo all\'area riservata.' }));
      } else if (codice === TROPPE_CONNESSIONI) {
        setTroppeConnessioni(true);
        rileggiERiavvia();
      } else if (ERRORI_ACCESSO.includes(codice) || rifiutoDelServer(err)) {
        // accesso cambiato o errore del server (ERRORE_INTERNO, ...): il client non riprova da solo
        rileggiERiavvia();
      }
    });

    sock.on('messaggio', (e: EvMessaggio) => {
      if (!e || !e.conversazioneId || !e.messaggio) return;
      fermaScrive(e.conversazioneId);
      const presente = stRef.current.conversazioni.some((c) => c.id === e.conversazioneId);
      modificaElenco(azioni.messaggio(e, mioIdPersona()));
      if (!presente) ricaricaConversazioni();
    });
    sock.on('messaggio_aggiornato', (e: EvMessaggio) => {
      if (e && e.conversazioneId && e.messaggio) modificaElenco(azioni.messaggioAggiornato(e));
    });
    sock.on('ricevute', (e: EvRicevute) => {
      if (e && e.conversazioneId) modificaElenco(azioni.ricevute(e));
    });
    sock.on('scrive', (e: EvScrive) => {
      if (!e || !e.conversazioneId) return;
      const id = e.conversazioneId;
      fermaScrive(id);
      dispatch(azioni.scrive(id, true, typeof e.nome === 'string' && e.nome ? e.nome : undefined));
      timerScrive.current.set(id, setTimeout(() => {
        timerScrive.current.delete(id);
        if (montato.current) dispatch(azioni.scrive(id, false));
      }, SCRIVE_MS));
    });
    sock.on('smette', (e: EvScrive) => {
      if (!e || !e.conversazioneId) return;
      fermaScrive(e.conversazioneId);
      dispatch(azioni.scrive(e.conversazioneId, false));
    });
    sock.on('presenza', (e: EvPresenza) => {
      if (e && e.associato) modificaElenco(azioni.presenza(e));
    });
    // Letta su un altro dispositivo o scheda: niente più non letti (la chat aperta qui non cambia altro)
    sock.on('conversazione_letta', (e: { conversazioneId?: string; lettoFino?: string | null }) => {
      if (e && e.conversazioneId) modificaElenco(azioni.azzeraNonLetti(e.conversazioneId));
    });
    sock.on('conversazione', (e: { conversazioneId?: string; conversazione?: ChatConversazione | null }) => {
      // la chat aperta è cambiata (per esempio sticker o pacchetti riservati del canale): il suo catalogo si rilegge
      const idEvento = e ? e.conversazioneId || (e.conversazione && e.conversazione.id) : null;
      if (idEvento && idEvento === stRef.current.attiva) chiediSticker(ritardoStickerCasuale());
      // vista completa (preferenze cambiate altrove, blocco): si sostituisce la riga senza ricaricare
      if (e && e.conversazione && e.conversazione.id) {
        modificaElenco(azioni.aggiornaConversazione(e.conversazione));
        return;
      }
      if (timerConversazioni.current) clearTimeout(timerConversazioni.current);
      timerConversazioni.current = setTimeout(() => {
        timerConversazioni.current = null;
        if (montato.current) ricaricaConversazioni();
      }, DEBOUNCE_CONVERSAZIONI_MS);
    });
    sock.on('conversazione_rimossa', (e: EvConversazione) => {
      if (e && e.conversazioneId) modificaElenco(azioni.rimuoviConversazione(e.conversazioneId));
    });
    // Catalogo degli sticker cambiato dall'ufficio ({ versione }): chi lo mostra lo rilegge, dopo un ritardo
    // casuale (l'evento arriva a tutti i client insieme, e l'ufficio ne manda uno per ogni modifica)
    sock.on(EVENTO_STICKER_AGGIORNATI, (e?: { versione?: string | null }) => {
      const versione = e && typeof e.versione === 'string' ? e.versione : null;
      if (versione && versione === ultimaVersioneSticker.current) return;
      if (versione) ultimaVersioneSticker.current = versione;
      chiediSticker(ritardoStickerCasuale());
    });

    return () => {
      corrente = false;
      segnalatore.ferma();
      fermaStabile();
      if (segnalatoreRef.current === segnalatore) segnalatoreRef.current = null;
      EVENTI_INTERAZIONE.forEach((ev) => document.removeEventListener(ev, interazione, { capture: true }));
      document.removeEventListener('scroll', scorrimento, { capture: true });
      window.removeEventListener('focus', interazione);
      window.removeEventListener('blur', sfocato);
      window.removeEventListener(EVENTO_PERSONA_CAMBIATA, cambioPersona);
      chiudi();
      if (timerRiavvio.current) {
        clearTimeout(timerRiavvio.current);
        timerRiavvio.current = null;
      }
      if (socketRef.current === sock) socketRef.current = null;
      if (inoltraRef.current === inoltra) inoltraRef.current = null;
      inoltrati.current = new Set();
      if (timerConversazioni.current) {
        clearTimeout(timerConversazioni.current);
        timerConversazioni.current = null;
      }
      timerScriveAttivi.forEach((t) => clearTimeout(t));
      timerScriveAttivi.clear();
      dispatch(azioni.connesso(false));
    };
  }, [abilitata, generazione, ricaricaConversazioni, leggiStato, fermaScrive, modificaElenco, sessioneScaduta, avviaStabile, fermaStabile, chiediSticker]);

  // Scheda nascosta: la chat aperta non si sta leggendo (il server conta i non letti e manda le notifiche)
  useEffect(() => {
    const cambio = () => {
      const nascosta = schedaNascosta();
      if (nascosta === stRef.current.nascosta) return;
      stRef.current = { ...stRef.current, nascosta };
      dispatch(azioni.nascosta(nascosta));
      // scheda nascosta: «non attivo» subito, così le push tornano al telefono
      if (segnalatoreRef.current) segnalatoreRef.current.visibilita(!nascosta);
      const attiva = stRef.current.attiva;
      if (attiva && socketRef.current) socketRef.current.emit('attiva', { conversazioneId: nascosta ? null : attiva });
      // backoff: si azzera solo dopo 5 minuti di connessione continua con la scheda visibile
      if (nascosta) fermaStabile();
      else if (stRef.current.connesso) avviaStabile();
      // tornata visibile con un riavvio in attesa (timer o attesa di visibilità): nuovo socket subito
      if (!nascosta && (attesaVisibilita.current || timerRiavvio.current)) {
        if (timerRiavvio.current) clearTimeout(timerRiavvio.current);
        timerRiavvio.current = null;
        attesaVisibilita.current = false;
        if (montato.current) setGenerazione((g) => g + 1);
      }
    };
    document.addEventListener('visibilitychange', cambio);
    return () => document.removeEventListener('visibilitychange', cambio);
  }, [avviaStabile, fermaStabile]);

  const aggiornaConversazione = useCallback((c: ChatConversazione) => modificaElenco(azioni.aggiornaConversazione(c)), [modificaElenco]);
  const rimuoviConversazione = useCallback((id: string) => modificaElenco(azioni.rimuoviConversazione(id)), [modificaElenco]);
  const azzeraNonLetti = useCallback((id: string) => modificaElenco(azioni.azzeraNonLetti(id)), [modificaElenco]);

  const segnaAttiva = useCallback((id: string | null) => {
    stRef.current = { ...stRef.current, attiva: id };
    dispatch(azioni.attiva(id));
    if (socketRef.current) socketRef.current.emit('attiva', { conversazioneId: stRef.current.nascosta ? null : id });
  }, []);

  const leggiBozza = useCallback((id: string) => bozze.current.get(id) || '', []);
  const leggiMenzioniBozza = useCallback((id: string) => menzioniBozze.current.get(id) || [], []);
  const salvaBozza = useCallback((id: string, testo: string, menzioni?: MenzioneComposta[]) => {
    if (testo) bozze.current.set(id, testo);
    else bozze.current.delete(id);
    if (testo && menzioni && menzioni.length) menzioniBozze.current.set(id, menzioni);
    else menzioniBozze.current.delete(id);
  }, []);

  const socket = useMemo<ChatSocket>(() => ({
    emit(ev: string, dati?: unknown) {
      if (socketRef.current) socketRef.current.emit(ev, dati);
    },
    on(ev: string, fn: (d: any) => void) {
      let fns = ascoltatori.current.get(ev);
      if (!fns) {
        fns = new Set();
        ascoltatori.current.set(ev, fns);
      }
      fns.add(fn);
      if (inoltraRef.current) inoltraRef.current(ev);
      return () => {
        const attuali = ascoltatori.current.get(ev);
        if (!attuali) return;
        attuali.delete(fn);
        if (attuali.size === 0) ascoltatori.current.delete(ev);
      };
    },
  }), []);

  const sottoscriviRisposte = useCallback((radiceId: string, cb: (e: EventoRisposta) => void) => {
    const ascolta = (tipo: EventoRisposta['tipo']) => socket.on(tipo, (e?: EvRisposta) => {
      if (e && e.radiceId === radiceId && e.risposta) cb({ tipo, risposta: e.risposta });
    });
    const via = [ascolta('risposta'), ascolta('risposta_aggiornata')];
    return () => via.forEach((fn) => fn());
  }, [socket]);

  const totaleNonLetti = useMemo(() => contaNonLetti(s.conversazioni), [s.conversazioni]);

  const valore = useMemo<ChatCtx>(() => ({
    stato: s.stato,
    caricamento: s.caricamento,
    elencoCaricato: s.elencoCaricato,
    errore: s.errore,
    conversazioni: s.conversazioni,
    totaleNonLetti,
    connesso: s.connesso,
    troppeConnessioni,
    scrivono: s.scrivono,
    nomiScrivono: s.nomiScrivono,
    attiva: s.attiva,
    ricaricaStato,
    ricaricaConversazioni,
    aggiornaConversazione,
    rimuoviConversazione,
    azzeraNonLetti,
    segnaAttiva,
    leggiBozza,
    leggiMenzioniBozza,
    salvaBozza,
    socket,
    sottoscriviRisposte,
    versioneSticker,
    ricaricaSticker,
  }), [s, totaleNonLetti, troppeConnessioni, ricaricaStato, ricaricaConversazioni, aggiornaConversazione, rimuoviConversazione, azzeraNonLetti, segnaAttiva, leggiBozza, leggiMenzioniBozza, salvaBozza, socket, sottoscriviRisposte, versioneSticker, ricaricaSticker]);

  return <ChatContext.Provider value={valore}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatCtx => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat va usato dentro <ChatProvider>');
  return ctx;
};
